/**
 * inputResolution — Input_Layer_Consolidation IC1.
 *
 * Canonical precedence registry for fields that exist in multiple input
 * layers (Project Intent / Typed Preferences / Site Context / SED).
 * Single source of truth — every consumer should route through
 * `resolveField(fieldId, sources)` instead of reading the layers
 * directly, so a user-visible value can never be inconsistent with
 * what the LLM prompt and the deterministic scorer actually saw.
 *
 * Design notes:
 *   - This file is a pure registry + resolver. No I/O, no React, no
 *     React Native — it must be safe to import from the typed-report
 *     generator (which runs server-side under Node) and from the
 *     LLM-prompt builder (which has no React context).
 *   - The resolver is intentionally narrow: one field at a time. Bulk
 *     "resolve every field for the prompt" runs through
 *     `buildInputLayersBlock` (IC4) which composes per-field calls so
 *     the audit trail (sourceLayer + sourceField) is preserved.
 *   - Cooling reads project through `normaliseCooling` (IC2) so the
 *     three legacy enum vocabularies all surface as a single canonical
 *     CoolingType. The original layer-specific string is still
 *     readable from the raw layer when needed — the resolver simply
 *     declares which one wins.
 *
 * Engineering rules (CLAUDE.md §5):
 *   - When adding a new INPUT_PRECEDENCE entry, also add a regression
 *     test in `__tests__/inputResolution.test.ts` pinning the layer
 *     order and a representative resolve result. The IC11 invariant
 *     suite asserts coverage so a missing test is detectable.
 */
import type { ProjectIntent, TypedPreferencesByType, SiteRagReport } from '../types/siteRag';
import type { SiteContext, ExtractedClaim, ClaimCategory } from '../types/siteContext';
import type { SiteEvidenceDocument } from '../types/siteEvidence';
import { hasDeclaredWaterSource } from './intent/waterSource';
import { isIntentFieldExplicitlyUnknown } from './intent/explicitUnknown';
import type { ReportDerivedClaim } from '../types/reportClaim';
import { normaliseCooling, type CoolingType } from '../types/cooling';
import { effectivePower, effectiveTimeline } from './projectIntent';

/* ── Layer identifiers ───────────────────────────────────────────── */

/**
 * The five authoritative input layers. Ordered first-to-last by
 * declaration only — actual precedence is encoded per-field on
 * `INPUT_PRECEDENCE[fieldId].layers`.
 *
 * RDX-5 widens the union with `'priorReport'` — the report-derived
 * tier that ranks between `'projectIntent'` (the user's stated ask,
 * still authoritative for buckets / ranges) and `'siteContext'`
 * (passive site-history claims). When a Feasibility report concluded
 * an exact MW figure but the user's intent only carries a bucket
 * ("50-200mw"), the prior report wins for the exact number while the
 * intent bucket continues to constrain the LLM's allowed range.
 */
export type InputLayer = 'reportPrefs' | 'projectIntent' | 'priorReport' | 'siteContext' | 'sed';

/* ── Field IDs ───────────────────────────────────────────────────── */

/**
 * Canonical field identifiers. Each represents a concept that appears
 * in two or more input layers. The string is the API surface the
 * consumer code uses to ask "what's the resolved value here?".
 *
 * Adding a field:
 *   1. Add the entry to `INPUT_PRECEDENCE` below with `layers` ordered
 *      most-authoritative first.
 *   2. Extend `readFromLayer` with the layer-specific reader.
 *   3. Pin a regression test.
 */
export type InputFieldId =
  | 'projectMW'
  | 'inServiceDate'
  | 'cooling'
  | 'riskPosture'
  | 'powerSource'
  | 'kpiWeights'
  | 'customerArchetype'
  | 'phasing'
  | 'carbonCommitment'
  | 'tierTarget'
  // P1 (field-architecture audit §3.6/§4) — SIX net-new BTM-feasibility intent
  // fields. Intent-only (no other layer carries them today), so user-edited
  // intent always wins — mirroring riskPosture / tierTarget.
  | 'interconnectionStatus'
  | 'fuelAccess'
  | 'waterSource'
  | 'offtake'
  | 'capacityCreditIntent'
  // FA-1 (v1.2.0) — BTM field-architecture axes. These are the
  // authoritative user-config leaves that the new 3-axis BTM model
  // (cooling generation / heat-rejection sink / power basis) reads. They
  // live ONLY on `projectIntent` (no per-report typed-prefs surface),
  // so a single intent-layer reader keeps them consistent across the
  // LLM prompt, the deterministic scorer and the report UI.
  | 'coolingGeneration'
  | 'bopHeatRejection'
  | 'powerBasis'
  | 'pueValue'
  | 'powerFactor';

/* ── Registry ────────────────────────────────────────────────────── */

export interface InputPrecedenceEntry {
  /** Ordered layer list; first non-undefined wins. */
  layers: ReadonlyArray<InputLayer>;
  /** Human-readable concept name for UI surfacing + prompt block. */
  conceptLabel: string;
  /** Influences deterministic score, narrative only, or both. */
  affects: 'score' | 'narrative' | 'both';
}

/**
 * Single source of truth for cross-layer precedence.
 *
 * SSOT — Site Project Intent is the canonical home for the six fields
 * that used to live on per-report `*Preferences` interfaces too. The
 * resolver now reads them from `projectIntent` only, with `siteContext`
 * as a soft fallback for legacy sites whose intent isn't fully
 * populated yet. `reportPrefs` has been dropped from the layer list
 * for the six SSOT-affected fields (projectMW / inServiceDate /
 * cooling / riskPosture / powerSource / customerArchetype) — the
 * fields no longer exist on the per-report typed-prefs interfaces, so
 * a `reportPrefs` layer entry would be unreachable.
 *
 * `kpiWeights`, `phasing`, `carbonCommitment`, `tierTarget` retain
 * their existing layer ordering — those fields are not SSOT-affected.
 */
export const INPUT_PRECEDENCE: Readonly<Record<InputFieldId, InputPrecedenceEntry>> = {
  projectMW: {
    // RDX-5 — `priorReport` ranks below `projectIntent` and above
    // `siteContext`. When the user's stated intent carries an EXACT MW
    // (`effectivePower().mw`), it still wins — the user is the highest
    // authority. When intent is bucket-only (no exact number) and a
    // prior Feasibility report concluded an exact number, the report
    // wins for downstream prompts. The intent bucket continues to
    // constrain the LLM's allowed range upstream of resolution.
    layers: ['projectIntent', 'priorReport', 'siteContext'],
    conceptLabel: 'Project size (MW)',
    affects: 'both',
  },
  inServiceDate: {
    layers: ['projectIntent', 'priorReport', 'siteContext'],
    conceptLabel: 'In-service date',
    affects: 'narrative',
  },
  cooling: {
    layers: ['projectIntent', 'priorReport', 'siteContext'],
    conceptLabel: 'Cooling strategy',
    affects: 'narrative',
  },
  riskPosture: {
    layers: ['projectIntent'],
    conceptLabel: 'Risk tolerance',
    affects: 'narrative',
  },
  powerSource: {
    layers: ['projectIntent', 'siteContext'],
    conceptLabel: 'Preferred power source',
    affects: 'narrative',
  },
  kpiWeights: {
    layers: ['reportPrefs', 'projectIntent'],
    conceptLabel: 'KPI weights',
    affects: 'score',
  },
  customerArchetype: {
    layers: ['projectIntent'],
    conceptLabel: 'Customer / archetype',
    affects: 'narrative',
  },
  phasing: {
    layers: ['projectIntent', 'reportPrefs'],
    conceptLabel: 'Phasing',
    affects: 'both',
  },
  carbonCommitment: {
    layers: ['projectIntent', 'reportPrefs'],
    conceptLabel: 'Carbon commitment',
    affects: 'narrative',
  },
  tierTarget: {
    layers: ['projectIntent'],
    conceptLabel: 'Datacenter tier target',
    affects: 'narrative',
  },
  // P1 (field-architecture audit §3.6/§4) — SIX net-new BTM-feasibility intent
  // fields. Intent-only precedence so a user-edited value reaches the
  // AUTHORITATIVE USER CONFIG block + wins over any future soft layer. All
  // narrative-affecting (they steer the development-risk / feasibility framing,
  // not the deterministic score).
  interconnectionStatus: {
    layers: ['projectIntent'],
    conceptLabel: 'Utility service / interconnection status',
    affects: 'narrative',
  },
  fuelAccess: {
    layers: ['projectIntent'],
    conceptLabel: 'Gas / fuel access',
    affects: 'narrative',
  },
  waterSource: {
    layers: ['projectIntent'],
    conceptLabel: 'Water source',
    affects: 'narrative',
  },
  offtake: {
    layers: ['projectIntent'],
    conceptLabel: 'Offtake / PPA structure',
    affects: 'narrative',
  },
  capacityCreditIntent: {
    layers: ['projectIntent'],
    conceptLabel: 'Capacity-credit (ELCC) intent',
    affects: 'narrative',
  },
  // FA-1 (v1.2.0) — BTM field-architecture axes. Intent-only: there is
  // no per-report typed-prefs surface for these, so the resolver reads
  // them straight off `projectIntent`. `affects:'narrative'` — they
  // shape the prompt / report basis blocks; the deterministic score
  // continues to consume the resolved power stack, not these raw leaves.
  coolingGeneration: {
    layers: ['projectIntent'],
    conceptLabel: 'Cooling generation (BTM machine axis)',
    affects: 'narrative',
  },
  bopHeatRejection: {
    layers: ['projectIntent'],
    conceptLabel: 'Heat-rejection sink (cold end)',
    affects: 'narrative',
  },
  powerBasis: {
    layers: ['projectIntent'],
    conceptLabel: 'Power basis (IT load vs facility)',
    affects: 'narrative',
  },
  pueValue: {
    layers: ['projectIntent'],
    conceptLabel: 'PUE (power usage effectiveness)',
    affects: 'narrative',
  },
  powerFactor: {
    layers: ['projectIntent'],
    conceptLabel: 'Power factor',
    affects: 'narrative',
  },
};

/* ── Sources bag ─────────────────────────────────────────────────── */

/**
 * The cross-layer source bag the resolver reads from. All fields are
 * optional so callers can pass partial sources (e.g. the wizard hasn't
 * snapshotted typed prefs yet).
 *
 * RDX-5 adds two optional channels for the `'priorReport'` layer:
 *   - `priorReports` — full report objects (when the caller already
 *     has them in hand, e.g. the wizard). Currently unused at the
 *     resolver level but reserved for future narrative-only readers.
 *   - `priorReportClaims` — flat list of `ReportDerivedClaim`s
 *     (typically the output of `listAllClaims()` from the RDX
 *     ingestion sub-collection). The resolver reads CURRENT claims
 *     from here when the `'priorReport'` layer is consulted for a
 *     field.
 *
 * Both are optional / absent on legacy callers — the `'priorReport'`
 * layer just returns `undefined` and falls through to the next layer.
 */
export interface InputSources {
  siteContext?: SiteContext | null;
  projectIntent?: ProjectIntent | null;
  reportPrefs?: TypedPreferencesByType | null;
  sed?: Partial<SiteEvidenceDocument> | null;
  /** RDX-5 — prior reports on this site (most-recent first, by createdAt). */
  priorReports?: ReadonlyArray<SiteRagReport> | null;
  /** RDX-5 — flat current+superseded claims sub-collection for this site. */
  priorReportClaims?: ReadonlyArray<ReportDerivedClaim> | null;
}

/* ── Resolved value shape ────────────────────────────────────────── */

/**
 * Result of `resolveField`. `value` is `null` when no layer carried a
 * non-empty value; `sourceLayer` + `sourceField` are populated when a
 * layer did. The string `sourceField` is suitable for surfacing in CT
 * pack's EvidenceLineage `kind:'user-amendment'` / `kind:'derived'`
 * cases without further composition.
 */
export interface ResolvedField<T> {
  value: T | null;
  sourceLayer: InputLayer | null;
  sourceField: string | null;
}

/* ── Per-field readers ───────────────────────────────────────────── */

/**
 * Read the value for `fieldId` from the named `layer`. Returns
 * `undefined` when the layer has nothing for that field (so the
 * resolver can fall through to the next layer). Per-field normalisation
 * (e.g. cooling enum projection) happens here so every reader sees the
 * canonical type.
 */
function readFromLayer<T>(
  layer: InputLayer,
  sources: InputSources,
  fieldId: InputFieldId,
): T | undefined {
  switch (fieldId) {
    case 'projectMW': {
      if (layer === 'projectIntent') {
        const ep = effectivePower(sources.projectIntent ?? undefined);
        return (ep.mw ?? undefined) as T | undefined;
      }
      // RDX-5 — prior-report claim on a known capacity field. Feasibility
      // writes `grid.estimatedCapacityMW` via the RDX registry; the
      // resolver reads it here so the next report on the same site
      // starts from the concluded number instead of the intent bucket.
      if (layer === 'priorReport') {
        const v = priorReportClaim(sources, [
          'grid.estimatedCapacityMW',
          'grid.capacityMW',
        ]);
        if (typeof v === 'number' && Number.isFinite(v)) return v as T;
        return undefined;
      }
      // SSOT — reportPrefs.feasibility.projectSizeMW and
      // reportPrefs['power-availability'].powerRequirementMW are deleted;
      // resolver returns projectIntent value (siteContext soft fallback).
      if (layer === 'siteContext') {
        return claimNumber(sources.siteContext ?? null, 'power.btm') as T | undefined;
      }
      return undefined;
    }
    case 'inServiceDate': {
      if (layer === 'projectIntent') {
        const et = effectiveTimeline(sources.projectIntent ?? undefined);
        return (et.inServiceDate ?? undefined) as T | undefined;
      }
      // RDX-5 — prior-report inferred in-service date (Feasibility
      // recommends conditions which sometimes lock the date). Today no
      // registry mapping writes this; the layer is wired for future
      // extension. Returns undefined so the resolver falls through.
      if (layer === 'priorReport') {
        const v = priorReportClaim(sources, [
          'siteResearch.feasibilityInServiceDate',
        ]);
        if (typeof v === 'string' && v.length > 0) return v as T;
        return undefined;
      }
      // SSOT — reportPrefs.feasibility.inServiceDate deleted.
      // PowerAvailability.timeToPowerCriticalDate is a distinct concept
      // (time-to-power) and is NOT a substitute for the site-canonical
      // in-service date.
      if (layer === 'siteContext') {
        return claimString(sources.siteContext ?? null, 'schedule.inservice') as T | undefined;
      }
      return undefined;
    }
    case 'cooling': {
      if (layer === 'projectIntent') {
        const raw = sources.projectIntent?.coolingStrategy;
        if (raw && raw !== 'undecided') return normaliseCooling(raw) as unknown as T;
        return undefined;
      }
      // RDX-5 — prior-report cooling recommendation. Today no registry
      // mapping populates a cooling field on the claims sub-collection;
      // the layer is wired for future Feasibility / Water-Resource
      // extensions that may.
      if (layer === 'priorReport') {
        const v = priorReportClaim(sources, [
          'siteResearch.recommendedCooling',
        ]);
        if (typeof v === 'string' && v.length > 0) return normaliseCooling(v) as unknown as T;
        return undefined;
      }
      // SSOT — reportPrefs.feasibility.coolingPreference and
      // reportPrefs['water-resource'].coolingType are deleted; cooling
      // resolves from projectIntent.coolingStrategy with siteContext as
      // soft fallback.
      if (layer === 'siteContext') {
        const claimVal = claimString(sources.siteContext ?? null, 'water.cooling')
          ?? claimString(sources.siteContext ?? null, 'technology.cooling');
        if (claimVal) return normaliseCooling(claimVal) as unknown as T;
        return undefined;
      }
      return undefined;
    }
    case 'riskPosture': {
      if (layer === 'projectIntent') {
        const p = sources.projectIntent?.priority;
        if (p && p !== 'undecided') return p as unknown as T;
        return undefined;
      }
      // SSOT — reportPrefs.feasibility.riskTolerance deleted; risk
      // posture lives canonically on projectIntent.priority.
      return undefined;
    }
    case 'powerSource': {
      if (layer === 'projectIntent') {
        const ni = sources.projectIntent?.nuclearInterest;
        if (ni && ni !== 'undecided') return ni as unknown as T;
        return undefined;
      }
      // SSOT — reportPrefs.feasibility.powerSourcePreference deleted.
      // PowerAvailability.btmStrategy is a distinct concept (grid vs BTM
      // sourcing strategy) and is NOT a substitute for projectIntent's
      // nuclear-interest signal — it stays on its own form/prefs surface.
      if (layer === 'siteContext') {
        return claimString(sources.siteContext ?? null, 'technology.reactor') as T | undefined;
      }
      return undefined;
    }
    case 'kpiWeights': {
      if (layer === 'reportPrefs') {
        const o = sources.reportPrefs?.feasibility?.kpiWeightOverrides;
        if (o && Object.keys(o).length > 0) return o as unknown as T;
        return undefined;
      }
      if (layer === 'projectIntent') {
        const cw = sources.projectIntent?.customWeights;
        return cw as unknown as T | undefined;
      }
      return undefined;
    }
    case 'customerArchetype': {
      if (layer === 'projectIntent') {
        const w = sources.projectIntent?.workloadProfile;
        if (w && w !== 'mixed') return w as unknown as T;
        return undefined;
      }
      // SSOT — reportPrefs.feasibility.customerCategory deleted;
      // customer archetype lives canonically on
      // projectIntent.workloadProfile.
      return undefined;
    }
    case 'phasing': {
      if (layer === 'projectIntent') {
        const ph = sources.projectIntent?.phases;
        if (ph && ph.length > 0) return ph as unknown as T;
        return undefined;
      }
      if (layer === 'reportPrefs') {
        const tf = sources.reportPrefs?.['test-fit'];
        if (tf?.phasing === 'phased' && typeof tf.phaseCount === 'number') {
          return { phasing: tf.phasing, phaseCount: tf.phaseCount } as unknown as T;
        }
        return undefined;
      }
      return undefined;
    }
    case 'carbonCommitment': {
      if (layer === 'projectIntent') {
        const c = sources.projectIntent?.carbonCommitment;
        if (c && c !== 'none') return c as unknown as T;
        return undefined;
      }
      return undefined;
    }
    case 'tierTarget': {
      if (layer === 'projectIntent') {
        const t = sources.projectIntent?.datacenterTierTarget;
        return t as unknown as T | undefined;
      }
      return undefined;
    }
    // P1 (field-architecture audit §3.6/§4) — SIX net-new BTM-feasibility intent
    // fields. Intent-only readers; an unset / pure-undecided sentinel
    // ('none' / 'undecided') resolves to undefined so the resolver reads as
    // "not declared" and never fabricates a value.
    case 'interconnectionStatus': {
      if (layer === 'projectIntent') {
        const v = sources.projectIntent?.interconnectionStatus;
        if (v && v.queueStatus && v.queueStatus !== 'none') return v as unknown as T;
        return undefined;
      }
      return undefined;
    }
    case 'fuelAccess': {
      if (layer === 'projectIntent') {
        const v = sources.projectIntent?.fuelAccess;
        if (v && v.supply && v.supply !== 'none') return v as unknown as T;
        return undefined;
      }
      return undefined;
    }
    case 'waterSource': {
      if (layer === 'projectIntent') {
        // MULTI-SELECT (2026-08-14): this read the DEPRECATED `.source`
        // scalar directly, against the standing rule in
        // lib/intent/waterSource.ts. The intent form STRIPS that scalar on
        // every write, so any site whose water source has been touched
        // resolved to undefined — and buildInputLayersBlock drops null
        // values, silently omitting `• Water source:` from the
        // '=== USER CONFIG (AUTHORITATIVE) ===' block that fronts the prompt.
        const v = sources.projectIntent?.waterSource;
        if (hasDeclaredWaterSource(v)) return v as unknown as T;
        return undefined;
      }
      return undefined;
    }
    case 'offtake': {
      if (layer === 'projectIntent') {
        const v = sources.projectIntent?.offtake;
        if (v && v.structure) return v as unknown as T;
        return undefined;
      }
      return undefined;
    }
    case 'capacityCreditIntent': {
      if (layer === 'projectIntent') {
        const v = sources.projectIntent?.capacityCreditIntent;
        if (v && v.target && v.target !== 'none') return v as unknown as T;
        return undefined;
      }
      return undefined;
    }
    // FA-1 (v1.2.0) — BTM field-architecture axes. Intent-only readers.
    // Each returns the explicit user-set leaf, or `undefined` when the
    // user has not stated it (so the resolver reports "no value" rather
    // than silently inventing a default — defaults live in powerStack.ts
    // / projectCoolingGeneration(), which are the derivation layer, not
    // the resolution layer).
    case 'coolingGeneration': {
      if (layer === 'projectIntent') {
        const cg = sources.projectIntent?.coolingGeneration;
        return cg as unknown as T | undefined;
      }
      return undefined;
    }
    case 'bopHeatRejection': {
      if (layer === 'projectIntent') {
        const hr = sources.projectIntent?.bopHeatRejection;
        if (hr && hr !== 'unknown') return hr as unknown as T;
        return undefined;
      }
      return undefined;
    }
    case 'powerBasis': {
      if (layer === 'projectIntent') {
        const pb = sources.projectIntent?.powerBasis;
        return pb as unknown as T | undefined;
      }
      return undefined;
    }
    case 'pueValue': {
      if (layer === 'projectIntent') {
        const pue = sources.projectIntent?.pueValue;
        if (typeof pue === 'number' && Number.isFinite(pue)) return pue as T;
        return undefined;
      }
      return undefined;
    }
    case 'powerFactor': {
      if (layer === 'projectIntent') {
        const pf = sources.projectIntent?.powerFactor;
        if (typeof pf === 'number' && Number.isFinite(pf)) return pf as T;
        return undefined;
      }
      return undefined;
    }
    default:
      return undefined;
  }
}

/* ── Public API ──────────────────────────────────────────────────── */

/**
 * Resolve a single cross-layer field through `INPUT_PRECEDENCE`.
 *
 * @example
 *   const r = resolveField<number>('projectMW', { projectIntent, reportPrefs });
 *   if (r.value != null) console.log(`${r.value} MW (source: ${r.sourceLayer})`);
 */
export function resolveField<T = unknown>(
  fieldId: InputFieldId,
  sources: InputSources,
): ResolvedField<T> {
  const spec = INPUT_PRECEDENCE[fieldId];
  if (!spec) {
    return { value: null, sourceLayer: null, sourceField: null };
  }
  for (const layer of spec.layers) {
    const value = readFromLayer<T>(layer, sources, fieldId);
    if (value !== undefined && value !== null) {
      return { value, sourceLayer: layer, sourceField: `${layer}.${fieldId}` };
    }
  }
  return { value: null, sourceLayer: null, sourceField: null };
}

/**
 * Resolve every entry in `INPUT_PRECEDENCE`. Used by IC4's
 * `buildInputLayersBlock` to emit the canonical [USER CONFIG] block at
 * the top of the LLM prompt. The shape preserves per-field provenance
 * so the block can label each line with its source layer.
 */
export function resolveAllFields(
  sources: InputSources,
): Record<InputFieldId, ResolvedField<unknown>> {
  const out: Partial<Record<InputFieldId, ResolvedField<unknown>>> = {};
  for (const fieldId of Object.keys(INPUT_PRECEDENCE) as InputFieldId[]) {
    out[fieldId] = resolveField(fieldId, sources);
  }
  return out as Record<InputFieldId, ResolvedField<unknown>>;
}

/* ── User-config resolution (honours explicit unknowns) ──────────── */

/**
 * The Project Intent `fieldSource` path whose explicit-unknown marker
 * (`'user-unknown'`, see `lib/intent/explicitUnknown.ts`) answers each
 * input field's `projectIntent` layer. Fields absent here (kpiWeights,
 * phasing, tierTarget, powerBasis) have no explicit-unknown capture.
 */
export const INPUT_FIELD_INTENT_PATH: Readonly<Partial<Record<InputFieldId, string>>> = {
  projectMW: 'powerRequirement',
  inServiceDate: 'timeline',
  cooling: 'coolingStrategy',
  riskPosture: 'priority',
  powerSource: 'nuclearInterest',
  customerArchetype: 'workloadProfile',
  carbonCommitment: 'carbonCommitment',
  interconnectionStatus: 'interconnectionStatus',
  fuelAccess: 'fuelAccess',
  waterSource: 'waterSource',
  offtake: 'offtake',
  capacityCreditIntent: 'capacityCreditIntent',
  coolingGeneration: 'coolingGeneration',
  bopHeatRejection: 'bopHeatRejection',
  pueValue: 'pueValue',
  powerFactor: 'powerFactor',
};

/**
 * `ResolvedField` plus the explicit-unknown answer. When
 * `explicitUnknownIntentField` is set, `value` is always null and
 * `sourceLayer` is `'projectIntent'`: the user's answer is "unknown".
 */
export interface UserConfigResolvedField<T> extends ResolvedField<T> {
  /** Intent field path the user recorded as unknown, else null. */
  explicitUnknownIntentField: string | null;
}

/**
 * Resolve one field for the report prompt's AUTHORITATIVE user-config
 * block. Identical to `resolveField` except that a Project Intent field
 * the user explicitly recorded as unknown STOPS resolution at the
 * `projectIntent` layer. Lower layers (prior-report conclusions, Site
 * Context claims, including document-extracted ones) are never promoted
 * into the authoritative slot in place of the user's "unknown" answer.
 * The explicit-unknown marker wins over any stale stored value, matching
 * `effectiveWorkloadProfile`.
 *
 * `resolveField` itself is unchanged: its other consumers (layer
 * warnings, coverage) keep today's fall-through semantics.
 */
export function resolveUserConfigField<T = unknown>(
  fieldId: InputFieldId,
  sources: InputSources,
): UserConfigResolvedField<T> {
  const spec = INPUT_PRECEDENCE[fieldId];
  const intentPath = INPUT_FIELD_INTENT_PATH[fieldId];
  if (
    spec
    && intentPath !== undefined
    && isIntentFieldExplicitlyUnknown(sources.projectIntent, intentPath)
  ) {
    for (const layer of spec.layers) {
      if (layer === 'projectIntent') {
        return {
          value: null,
          sourceLayer: 'projectIntent',
          sourceField: `projectIntent.${fieldId}`,
          explicitUnknownIntentField: intentPath,
        };
      }
      // A layer ranked ABOVE projectIntent still wins as it does today.
      const value = readFromLayer<T>(layer, sources, fieldId);
      if (value !== undefined && value !== null) {
        return { value, sourceLayer: layer, sourceField: `${layer}.${fieldId}`, explicitUnknownIntentField: null };
      }
    }
  }
  return { ...resolveField<T>(fieldId, sources), explicitUnknownIntentField: null };
}

/** `resolveAllFields` for the user-config prompt block (see `resolveUserConfigField`). */
export function resolveAllUserConfigFields(
  sources: InputSources,
): Record<InputFieldId, UserConfigResolvedField<unknown>> {
  const out: Partial<Record<InputFieldId, UserConfigResolvedField<unknown>>> = {};
  for (const fieldId of Object.keys(INPUT_PRECEDENCE) as InputFieldId[]) {
    out[fieldId] = resolveUserConfigField(fieldId, sources);
  }
  return out as Record<InputFieldId, UserConfigResolvedField<unknown>>;
}

/* ── Site Context claim readers ──────────────────────────────────── */

/**
 * Resolve the first claim of `category` to a numeric value. Returns
 * `undefined` when no such claim exists or its value isn't numeric.
 */
function claimNumber(ctx: SiteContext | null, category: ClaimCategory): number | undefined {
  const c = firstClaim(ctx, category);
  const v = c?.claimedValue;
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return undefined;
}

function claimString(ctx: SiteContext | null, category: ClaimCategory): string | undefined {
  const c = firstClaim(ctx, category);
  const v = c?.claimedValue;
  if (typeof v === 'string' && v.trim().length > 0) return v.trim();
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return undefined;
}

function firstClaim(ctx: SiteContext | null, category: ClaimCategory): ExtractedClaim | undefined {
  if (!ctx?.claims || ctx.claims.length === 0) return undefined;
  return ctx.claims.find(c => c.category === category && c.claimedValue !== undefined);
}

/* ── RDX-5 — Prior-report claim reader ───────────────────────────── */

/**
 * RDX-5 — pull the latest non-superseded `ReportDerivedClaim` for any
 * of the supplied `sedFieldPath`s out of an `InputSources` bag. The
 * `pathCandidates` list is ordered: the first path that returns a
 * current claim wins. Returns `undefined` when nothing matches —
 * `readFromLayer` then falls through to the next precedence layer.
 *
 * Deterministic across rebuilds: when multiple current claims exist
 * (no supersession run), the latest `createdAt` wins with `reportId`
 * as a stable tie-break. Mirrors `selectCurrentReportClaim` in
 * `sedDocResolver`.
 *
 * Returns the raw `value` (unknown). Callers narrow per field.
 */
export function priorReportClaim(
  sources: InputSources,
  pathCandidates: ReadonlyArray<string>,
): unknown {
  const claims = sources.priorReportClaims ?? [];
  if (claims.length === 0) return undefined;
  for (const path of pathCandidates) {
    const current = claims
      .filter(c => c.sedFieldPath === path && c.supersededBy == null)
      .sort((a, b) => {
        if (b.createdAt !== a.createdAt) return b.createdAt - a.createdAt;
        return a.reportId.localeCompare(b.reportId);
      });
    if (current.length > 0) return current[0]!.value;
  }
  return undefined;
}

/* ── RDX-5 — Contradiction detection ─────────────────────────────── */

/**
 * RDX-5 — explicit contradiction record between a higher-precedence
 * layer (typically `projectIntent`) and the `priorReport` layer.
 * Surfaces in the UI's "Configuration inherited" chip as a warning
 * affordance: "Feasibility report concluded 145 MW — using 100 (intent
 * wins), deviates by 45 MW".
 *
 * Issued ONLY when both layers carry a non-empty value AND they
 * disagree. Same field-id taxonomy as `LayerDisagreement` in
 * `inputLayerWarnings.ts`; carried here because the resolver is the
 * single source of truth for layer values and the disagreement test
 * shares the same per-field reader.
 */
export interface PriorReportDeviation {
  fieldId: InputFieldId;
  /** Concept label for UI surfacing. */
  conceptLabel: string;
  /** The winning (resolver-returned) layer + value. */
  winningLayer: InputLayer;
  winningValue: unknown;
  /** The prior-report value the resolver did NOT pick. */
  priorReportValue: unknown;
  /** The originating report id, when known. */
  priorReportId?: string;
  /** Human-readable "deviates by X" delta for numeric fields. */
  deviation?: string;
}

/**
 * Detect deviations between the resolved value and any prior-report
 * claim for the same field. Returns an empty array when no prior-
 * report claims are present OR when the prior report agrees with
 * (or won) resolution.
 *
 * Used by the wizard's "Configuration inherited" chip and the
 * forthcoming prompt block to surface "Feasibility concluded 145 MW,
 * deviates from intent 100". Pure / synchronous — no I/O.
 */
export function detectPriorReportDeviations(
  sources: InputSources,
): PriorReportDeviation[] {
  const out: PriorReportDeviation[] = [];
  if (!sources.priorReportClaims || sources.priorReportClaims.length === 0) return out;

  for (const fieldId of Object.keys(INPUT_PRECEDENCE) as InputFieldId[]) {
    const spec = INPUT_PRECEDENCE[fieldId];
    if (!spec.layers.includes('priorReport')) continue;

    const winning = resolveField(fieldId, sources);
    if (winning.value == null) continue;
    if (winning.sourceLayer === 'priorReport') continue; // report won — no deviation

    const reportValue = readFromLayer<unknown>('priorReport', sources, fieldId);
    if (reportValue == null) continue;

    // Compare with loose equality — numbers within 1% agree, strings
    // are case-insensitive trimmed.
    if (looseEqual(winning.value, reportValue)) continue;

    // Find the underlying claim for the originating-report id.
    let priorReportId: string | undefined;
    for (const claim of sources.priorReportClaims) {
      if (claim.supersededBy != null) continue;
      // Match by value (handles registry without a single dot-path map).
      if (looseEqual(claim.value, reportValue)) {
        priorReportId = claim.reportId;
        break;
      }
    }

    let deviation: string | undefined;
    if (typeof winning.value === 'number' && typeof reportValue === 'number') {
      const delta = winning.value - reportValue;
      const sign = delta === 0 ? '' : delta > 0 ? '+' : '';
      deviation = `${sign}${delta.toFixed(0)}`;
    }

    out.push({
      fieldId,
      conceptLabel: spec.conceptLabel,
      winningLayer: winning.sourceLayer ?? 'projectIntent',
      winningValue: winning.value,
      priorReportValue: reportValue,
      priorReportId,
      deviation,
    });
  }

  return out;
}

function looseEqual(a: unknown, b: unknown): boolean {
  if (a == null || b == null) return false;
  if (typeof a === 'number' && typeof b === 'number') {
    if (!Number.isFinite(a) || !Number.isFinite(b)) return false;
    if (a === 0 && b === 0) return true;
    const denom = Math.max(Math.abs(a), Math.abs(b));
    if (denom === 0) return true;
    return Math.abs(a - b) / denom <= 0.01;
  }
  if (typeof a === 'string' && typeof b === 'string') {
    return a.trim().toLowerCase() === b.trim().toLowerCase();
  }
  return a === b;
}

/* ── Re-export cooling type for convenience ──────────────────────── */

export type { CoolingType } from '../types/cooling';
export { normaliseCooling } from '../types/cooling';
